import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp, Trash2, Bell, KeyRound, UserPlus, UserMinus } from 'lucide-react';
import { 
  collection, addDoc, onSnapshot, query, where, updateDoc, doc, orderBy, limit 
} from "firebase/firestore";
import { db } from '../../firebase'; 
import './AdminArea.css';
import Facturas from './FacturasADMIN';
import NotificacionesADMIN from './NotificacionesADMIN'; 

const rolesDisponibles = ['Admin', 'Editor', 'Viewer'];

const sections = [
  { id: 'facturas', label: 'Facturas', icon: <UserPlus size={18} /> },
  { id: 'notificaciones', label: 'Notificaciones', icon: <Bell size={18} /> },
  { id: 'clientes', label: 'Clientes', icon: <UserMinus size={18} /> },
  { id: 'configuraciones', label: 'Configuraciones', icon: <KeyRound size={18} /> }
];

const AdminArea = () => {
  const [openSection, setOpenSection] = useState(sections[0].id); // Por defecto abrir la primera

  // Estados para datos
  const [clientes, setClientes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [notificacionTexto, setNotificacionTexto] = useState('');
  const [notificaciones, setNotificaciones] = useState([]);
  const [usuariosAcceso, setUsuariosAcceso] = useState([]);
  const [logsAcceso, setLogsAcceso] = useState([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [nuevoRol, setNuevoRol] = useState('');

  useEffect(() => {
    // Firestore listeners (igual que antes)
    const unsubClientes = onSnapshot(collection(db, "clientes"), snapshot => {
      const clientesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setClientes(clientesData);
    });

    const qNoti = query(collection(db, "notificaciones"), orderBy("fecha", "desc"), limit(50));
    const unsubNotificaciones = onSnapshot(qNoti, snapshot => {
      const notiData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setNotificaciones(notiData);
    });

    const unsubUsuarios = onSnapshot(collection(db, "usuariosAcceso"), snapshot => {
      const usuariosData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setUsuariosAcceso(usuariosData);
    });

    const qLogs = query(collection(db, "logsAcceso"), orderBy("fecha", "desc"), limit(50));
    const unsubLogs = onSnapshot(qLogs, snapshot => {
      const logsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setLogsAcceso(logsData);
    });

    return () => {
      unsubClientes();
      unsubNotificaciones();
      unsubUsuarios();
      unsubLogs();
    };
  }, []);

  const toggleSection = (id) => {
    setOpenSection(id);
  };

  // Los métodos (agregarCliente, eliminarCliente, enviarNotificacion, handleCambiarRol) igual que antes...

  const agregarCliente = async () => {
    try {
      const nuevoCliente = {
        nombre: `Cliente${Date.now()}`,
        email: `cliente${Date.now()}@duxb.ite`
      };
      await addDoc(collection(db, "clientes"), nuevoCliente);
    } catch (error) {
      alert('Error al agregar cliente: ' + error.message);
    }
  };

  const eliminarCliente = async (id) => {
    try {
      await updateDoc(doc(db, "clientes", id), { eliminado: true });
    } catch (error) {
      alert('Error al eliminar cliente: ' + error.message);
    }
  };

  const enviarNotificacion = async () => {
    if (notificacionTexto.trim() === '') return alert('Escribe algo para enviar.');
    try {
      await addDoc(collection(db, "notificaciones"), {
        texto: notificacionTexto,
        fecha: new Date()
      });
      setNotificacionTexto('');
      alert('Notificación enviada!');
    } catch (error) {
      alert('Error al enviar notificación: ' + error.message);
    }
  };

  const handleCambiarRol = async () => {
    if (!usuarioSeleccionado || !nuevoRol) return alert('Selecciona usuario y rol.');
    try {
      const userRef = doc(db, "usuariosAcceso", usuarioSeleccionado);
      await updateDoc(userRef, { rol: nuevoRol });

      await addDoc(collection(db, "logsAcceso"), {
        usuario: usuariosAcceso.find(u => u.id === usuarioSeleccionado)?.nombre || 'Desconocido',
        fecha: new Date(),
        accion: `Rol cambiado a ${nuevoRol}`
      });

      alert('Rol actualizado con éxito');
    } catch (error) {
      alert('Error al actualizar rol: ' + error.message);
    }
  };

  const clientesFiltrados = clientes.filter(c =>
    c.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
    c.email.toLowerCase().includes(busqueda.toLowerCase())
  );

  return (
    <main className="adminAreaContainer" role="main" style={{ display: 'flex', height: '90vh', gap: '1rem' }}>
      {/* Menú Lateral */}
      <nav 
        className="adminMenu"
        style={{
          width: '250px',
          background: '#0d47a1',
          color: '#fff',
          borderRadius: '8px',
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          userSelect: 'none'
        }}
      >
        <h1 style={{ margin: '0 0 1rem', fontSize: '1.5rem', fontWeight: 'bold' }}>Admin Panel</h1>
        {sections.map(({ id, label, icon }) => (
          <button
            key={id}
            onClick={() => toggleSection(id)}
            style={{
              background: openSection === id ? '#1976d2' : 'transparent',
              border: 'none',
              color: 'inherit',
              cursor: 'pointer',
              padding: '12px 16px',
              borderRadius: '6px',
              fontWeight: openSection === id ? 'bold' : 'normal',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              transition: 'background 0.3s ease'
            }}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </nav>

      {/* Contenido */}
      <section
        className="adminContent"
        style={{
          flex: 1,
          background: '#fff',
          borderRadius: '8px',
          padding: '1.5rem',
          overflowY: 'auto',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
        }}
      >
        {openSection === 'facturas' && <Facturas />}

        {openSection === 'notificaciones' && (
          <NotificacionesADMIN
            notificaciones={notificaciones}
            setNotificaciones={setNotificaciones}
          />
        )}

        {openSection === 'clientes' && (
          <>
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              style={{
                width: '100%',
                padding: '8px',
                borderRadius: '8px',
                marginBottom: '1rem',
                border: '1px solid #ccc',
                fontSize: '1rem',
              }}
            />
            <button className="btnPrimary" onClick={agregarCliente}>Agregar cliente</button>
            <ul style={{ marginTop: '1rem', maxHeight: '60vh', overflowY: 'auto', paddingLeft: '20px' }}>
              {clientesFiltrados.length ? (
                clientesFiltrados.map(cliente => (
                  <li key={cliente.id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span>{cliente.nombre} - {cliente.email}</span>
                    <button
                      className="btnDanger"
                      onClick={() => eliminarCliente(cliente.id)}
                      title="Eliminar cliente"
                    >
                      <Trash2 size={16} />
                    </button>
                  </li>
                ))
              ) : (
                <li>No se encontraron clientes.</li>
              )}
            </ul>
          </>
        )}

        {openSection === 'configuraciones' && (
          <>
            <h3>Seguridad y Acceso</h3>
            <section style={{ marginBottom: '1.5rem', backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}>
              <h4 style={{ borderBottom: '2px solid #0070f3', paddingBottom: '6px' }}>Usuarios con acceso</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#0070f3', color: 'white' }}>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Nombre</th>
                    <th style={{ padding: '8px', textAlign: 'left' }}>Rol</th>
                  </tr>
                </thead>
                <tbody>
                  {usuariosAcceso.map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px' }}>{user.nombre}</td>
                      <td style={{ padding: '8px' }}>{user.rol}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>

            <section style={{ marginBottom: '1.5rem' }}>
              <h4>Cambiar rol de usuario</h4>
              <select
                value={usuarioSeleccionado || ''}
                onChange={e => setUsuarioSeleccionado(e.target.value)}
                style={{ marginRight: '10px', padding: '6px', borderRadius: '6px' }}
              >
                <option value="" disabled>Selecciona usuario</option>
                {usuariosAcceso.map(u => (
                  <option key={u.id} value={u.id}>{u.nombre}</option>
                ))}
              </select>
              <select
                value={nuevoRol}
                onChange={e => setNuevoRol(e.target.value)}
                style={{ marginRight: '10px', padding: '6px', borderRadius: '6px' }}
              >
                <option value="" disabled>Selecciona rol</option>
                {rolesDisponibles.map(rol => (
                  <option key={rol} value={rol}>{rol}</option>
                ))}
              </select>
              <button className="btnPrimary" onClick={handleCambiarRol}>Actualizar rol</button>
            </section>

            <section>
              <h4>Logs de acceso recientes</h4>
              <ul style={{ maxHeight: '200px', overflowY: 'auto', paddingLeft: '20px' }}>
                {logsAcceso.length ? (
                  logsAcceso.map(log => (
                    <li key={log.id} style={{ marginBottom: '6px', fontSize: '0.9rem' }}>
                      <strong>{log.usuario}</strong> — {log.accion} — <em>{new Date(log.fecha.seconds * 1000).toLocaleString()}</em>
                    </li>
                  ))
                ) : (
                  <li>No hay logs recientes.</li>
                )}
              </ul>
            </section>
          </>
        )}
      </section>
    </main>
  );
};

export default AdminArea;
